import React, { ReactElement, useRef, useEffect, useMemo } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import Button from '../../atoms/buttons/Button';
import ThreadEntry from '../../atoms/mail/ThreadEntryV2';
import { Select } from '../../atoms/Select';
import { renderAvatar, tsSince } from '../../common';
import _isEmpty from 'lodash/isEmpty';
import { getBrowserDetails, isDarkMode, isUUID } from '../../utils/helpers';
import { GENERAL_MESSAGE_AVATAR, GENERAL_MESSAGE_USERNAME } from '../../config/Master';
import LoadingBackdrop from '../../atoms/loader/LoadingBackdrop';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../store';
import { filterThreads, handleThreadClick } from '../../actions/mailActions';
import useMailFetchActions from './hooks/useMailFetchActions';
import { THREADS_ORDER } from '../../constants/threads';
import breakpoints from '../../theme/material/breakpoints';
import { getBackgroundTheme, getBorders, getTextColor } from 'style';
import { FONT_FAMILIES } from 'theme/material/constants';
import { CustomStyledProps } from 'style/style';

const ThreadsContainer = styled('div')(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    [breakpoints.down(768.05)]: {
      height: 'fit-content',
      border: getBorders().PRIMARY,
      borderRadius: '10px',
    },
  }),
  ({ threadsList }: CustomStyledProps) =>
    threadsList?.length === 0
      ? {
          minHeight: 525,
          borderRadius: '10px !important',
        }
      : {}
);

const ThreadList = styled('div')({
  overflow: 'hidden',
  overflowY: 'scroll',
  height: `calc(${getBrowserDetails().isIpad && screen.height > 1024 ? '87vh' : '100vh'} - 140px)`,

  [breakpoints.down(768.05)]: {
    maxHeight: 'calc(100vh - 244px)',
    height: 'auto',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
  },
});

const ThreadHeader = styled('div')(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    borderBottom: getBorders().PRIMARY,
    borderRight: getBorders().PRIMARY,
    background: isDarkMode()
      ? 'linear-gradient(180deg, #2C5456 0%, #212121 100%)'
      : 'linear-gradient(180deg, #E6F6F7 0%, #FFF 100%)',
    padding: '20px 15px',
    justifyContent: 'space-between',
    borderTopLeftRadius: '10px',
    [breakpoints.down(768.05)]: {
      borderRight: 'none',
      alignSelf: 'stretch',
      borderTopLeftRadius: '10px',
      borderTopRightRadius: '10px',
    },
  })
);

const ThreadTitle = styled('span')(
  (): CSSObject => ({
    fontFamily: FONT_FAMILIES.secondary,
    fontSize: 20,
    color: getTextColor().DARK_4.color,
    lineHeight: '20px',
    letterSpacing: '-0.2px',
  })
);

interface ThreadsProps {
  openThreadHashId: any;
  fetching: any;
  order: any;
  canLoadMore: any;
  openConversation: boolean;
  showPwaInstallPromptBanner: boolean;
  themeDarkMode?: boolean;
}

const Threads = ({
  openThreadHashId,
  fetching,
  order,
  canLoadMore,
  openConversation,
  showPwaInstallPromptBanner,
}: ThreadsProps): ReactElement => {
  const fetchingMore = useSelector((state: RootState) => state.mail.fetchingMoreThreads);
  const threads = useSelector((state: RootState) => state.mail.threads);

  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();

  const collection = useMemo(() => threads.collection ?? [], [threads?.collection]);
  const meta = useMemo(() => threads?.meta ?? {}, [threads?.meta]);

  const { fetchThreads } = useMailFetchActions();

  const threadRef = useRef(null);

  const navigate = useNavigate();

  const onThreadChange = e => {
    const order = e.target.value;

    dispatch(filterThreads(navigate, location, params));

    navigate(`/mail${order === 'recent' ? '' : `/${order}`}`);
  };

  useEffect(() => {
    const handleResize = () => {
      if (!threadRef.current) {
        return;
      }

      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const offsetTop = threadRef?.current?.offsetTop + 80;

      if (screenWidth <= 375 && !openConversation) {
        const pwaBannerInstallPromptHeight = showPwaInstallPromptBanner ? 130 : 0;
        const height = screenHeight - (offsetTop - pwaBannerInstallPromptHeight);

        threadRef.current.style.height = `${height}px`;
        document.body.classList.add('body-scroll-disabled');
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('body-scroll-disabled');
    };
  }, [threadRef, openConversation, showPwaInstallPromptBanner]);

  const renderThreads = () => {
    return collection.map(({ locked, last_message, mailbox_default_sort_at, other_profile, admin, ...item }, index) => {
      const otherProfile = other_profile?.data || {};
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      const otherAccount = _find(['account', 'data'], otherProfile) || {};
      const avatar = admin ? GENERAL_MESSAGE_AVATAR : renderAvatar(otherProfile.avatar.data.urls, true);
      const lastMessage = last_message?.data || {};
      const mailboxDefaultSortDate = mailbox_default_sort_at?.date || '';
      const username = admin ? GENERAL_MESSAGE_USERNAME : otherAccount.username || '';
      const isSelected = isUUID(openThreadHashId)
        ? item.uid === openThreadHashId
        : otherAccount.hash_id === openThreadHashId;
      // Only consider it the last entry if it's the last thread AND there's no "Load more" button AND not fetching
      const isLastEntry = index === collection.length - 1 && !canLoadMore && !fetching;

      return (
        <ThreadEntry
          isDeleted={
            lastMessage.to_status === 'deleted' ||
            lastMessage.status === 'deleted' ||
            lastMessage.is_deleted ||
            lastMessage.is_visible === false
          }
          isOnline={otherProfile.online}
          key={item.id}
          username={username}
          avatar={avatar}
          message={lastMessage.message ? lastMessage.message : ''}
          isPhoto={lastMessage.type && lastMessage.type === 'photo'}
          iReplied={!_isEmpty(lastMessage) && lastMessage.message && lastMessage.hash_id !== otherAccount.hash_id}
          isNew={lastMessage.status === 'new' || item.status === 'new'}
          isLocked={locked}
          isUnread={!_isEmpty(lastMessage) && lastMessage.id && !lastMessage.i_read}
          lastMsgDate={tsSince(mailboxDefaultSortDate || (item.updated_at && item.updated_at.date), false, false)}
          onThreadClick={() => dispatch(handleThreadClick(item.id, otherAccount.hash_id, params, navigate, item.uid))}
          selected={isSelected}
          isSeveredAccount={item.deleted_reason === 'severed'}
          accountType={otherProfile.account_type}
          admin={admin}
          isLastEntry={isLastEntry}
        />
      );
    });
  };

  return (
    <ThreadsContainer threadsList={collection}>
      <ThreadHeader>
        <ThreadTitle>Conversations</ThreadTitle>
        <Select
          onChange={onThreadChange}
          value={order}
          style={{ paddingBottom: 0, marginRight: 0 }}
          id="mail_order_dropdown"
          baseSelectStyle={{
            width: '127px',
            height: '31px',
            borderRadius: '3px',
          }}
          customSelectStyle={{
            background: getBackgroundTheme().block,
          }}
          dropdownContainerCustomStyle={{
            height: '31px',
          }}
          customDropDownStyle={{
            height: 11,
            width: 92,
            padding: '9px 27px 9px 6px',
            color: getTextColor().LIGHT_GRAY.color,
            fontFamily: FONT_FAMILIES.primary,
            fontSize: '15px',
            fontWeight: 400,
            lineHeight: '11px',
            letterSpacing: '-0.3px',
            textAlign: 'right',
          }}
          noSpacing
        >
          {THREADS_ORDER.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </ThreadHeader>
      <ThreadList ref={threadRef}>
        {(!fetching || fetchingMore) && renderThreads()}
        {!fetching && canLoadMore && (
          <Button
            onClick={() => fetchThreads(meta?.pagination?.current_page + 1)}
            style={{
              width: '100%',
              marginTop: 10,
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              border: 'none',
              marginBottom: 0,
            }}
          >
            Load older...
          </Button>
        )}
      </ThreadList>

      {fetching && (
        <LoadingBackdrop
          active={true}
          showLoadingBg={false}
          customStyle={{
            height: 'calc(100vh - 65px) !important',
            overflowY: 'hidden',
            zIndex: 1050,
            [breakpoints.down(768.05)]: {
              marginTop: -18,
              height: 'calc(100vh - 48px) !important',
            },
          }}
        />
      )}
    </ThreadsContainer>
  );
};

export default React.memo(Threads);
